import Box from '@mui/material/Box';
import { InvoiceCategoryFilter } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useContext, useEffect, useMemo } from 'react';
import { useState } from 'react';

import InvoicesTabContent from '~components/Invoice/InvoicesTabContent';
import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { SmallTabs } from '~components/Tabs/SmallTabs';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { Invoice } from '~hooks/useInvoices/models';
import useInvoices from '~hooks/useInvoices/useInvoices';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import JobSummary from '~pages/Approvals/ApproveJobs/components/ApproveJobsDetailsTabs/JobDetails';
import DriverPayPageContext from '~pages/Approvals/DriverPayV2/DriverPayPageContext';
import DriverPayQueryContext from '~pages/Approvals/DriverPayV2/DriverPayQueryContext';
import { LoadsQueryContextProvider } from '~pages/Approvals/Files/components/JobsTable/ExpandedBlock/LoadsQueryContext';
import LoadsTab from '~pages/Approvals/Files/components/JobsTable/ExpandedBlock/LoadsTab';
import { JobInvoices } from '~pages/Dispatch/JobDetailsContainer';
import { useStores } from '~store/RootStore';
import { LoadsJobFilters } from '~types/loadsFilters';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import InvoicesQueryContext from '../../OrdersList/ExpandedBlock/InvoicesQueryContext';

interface ExpandedBlockProps {
  invoice: Invoice;
}

export default function ExpandedBlock({ invoice }: ExpandedBlockProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const [job, setJob] = useState<Job | null>(null);
  const { getJobById, isLoading } = useJob();
  const { currencyFormatter } = useCompanyCurrency();
  const { approveInvoice, unapproveInvoice } = useInvoices();
  const { currentCategory } = useContext(DriverPayPageContext);
  const { updateInvoice } = useContext(InvoicesQueryContext);

  // Get filters from DriverPayQueryContext to pass to LoadsQueryContextProvider
  const driverPayQueryContext = useContext(DriverPayQueryContext);
  const loadsFilters = useMemo((): LoadsJobFilters => {
    const contextFilters = driverPayQueryContext.filters;
    return {
      driverIds: contextFilters.driverIds,
      dropoffSiteIds: contextFilters.dropoffSiteIds,
      pickupSiteIds: contextFilters.pickupSiteIds,
      states: contextFilters.jobStates,
      vendorAccountIds: contextFilters.vendorAccountIds,
    };
  }, [driverPayQueryContext.filters]);

  const jobId = invoice.job?.id;

  const summaryTabId = `summary_${jobId}`;

  useEffect(() => {
    const fetchJob = async () => {
      if (jobId) {
        const jobData = await getJobById(jobId, companyId);

        if (jobData) {
          setJob(jobData);
        }
      }
    };
    fetchJob();
  }, [jobId, companyId]);

  const onApproveInvoice = async () => {
    const updatedInvoice = await approveInvoice(invoice);
    updateInvoice(updatedInvoice);
  };

  const onUnapproveInvoice = async () => {
    const updatedInvoice = await unapproveInvoice(invoice);
    updateInvoice(updatedInvoice);
  };

  const tabs = useMemo(() => {
    return [
      {
        id:
          currentCategory === InvoiceCategoryFilter.RECEIVABLES
            ? t('page_headings.driver_day_receivables')
            : t('dispatch.job.payables'),
        headerClassName: 'jobs-tab',
        label:
          currentCategory === InvoiceCategoryFilter.RECEIVABLES
            ? t('page_headings.driver_day_receivables')
            : t('dispatch.job.payables'),
        content: (
          <>
            <LoadingSpinner isVisible={isLoading} />
            {job && (
              <InvoicesTabContent
                invoice={invoice}
                job={job}
                utils={{
                  currencyFormatter: currencyFormatter,
                  approveInvoice: onApproveInvoice,
                  unapproveInvoice: onUnapproveInvoice,
                }}
                customCategory={currentCategory}
                isDayDetailsView
              />
            )}
          </>
        ),
      },
      ...(job
        ? [
            ...(currentCategory === InvoiceCategoryFilter.RECEIVABLES
              ? [
                  {
                    id: `payables_${jobId}`,
                    label: t('dispatch.job.payables'),
                    content: (
                      <JobInvoices
                        currentCategory={InvoiceCategoryFilter.PAYABLES}
                        hideMap
                        job={job}
                        reloadAfterApproved
                      />
                    ),
                  },
                ]
              : [
                  {
                    id: `receivables_${jobId}`,
                    label: t('dispatch.job.receivables'),
                    content: (
                      <JobInvoices
                        currentCategory={InvoiceCategoryFilter.RECEIVABLES}
                        hideMap
                        job={job}
                        reloadAfterApproved
                      />
                    ),
                  },
                ]),
            {
              id: summaryTabId,
              label: t('common.job_summary'),
              content: <JobSummary job={job} includeTimelineInJobDetailsTab />,
            },
            {
              id: `loads_${jobId}`,
              label: t('order.form.loads'),
              content: (
                <LoadsQueryContextProvider
                  companyId={companyId}
                  filters={loadsFilters}
                  job={job}
                >
                  <LoadsTab />
                </LoadsQueryContextProvider>
              ),
            },
          ]
        : []),
    ];
  }, [companyId, currentCategory, invoice, isLoading, job, loadsFilters]);

  return (
    <Box sx={(theme) => ({ bgcolor: theme.brandV2.colors.treadGray8, p: 1.5 })}>
      <SmallTabs tabs={tabs} />
    </Box>
  );
}
